import dayjs, { type Dayjs } from "dayjs"

import { surfaceKey } from "../../User/redux/ui-preferences.types.ts"

export type WeekdayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 // Sunday=0 ... Saturday=6, matching FullCalendar's own convention (Calendar.tsx firstDay={0}, hiddenDays).

export const ALL_WEEKDAYS: WeekdayIndex[] = [0, 1, 2, 3, 4, 5, 6]

export const WEEKDAY_LABELS: Record<WeekdayIndex, string> = {
  0: "Sunday",
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday"
}

export interface DaySchedule {
  enabled: boolean
  start: string // "HH:mm", 24h
  end: string
}

export interface WorkingScheduleBreak {
  enabled: boolean
  start: string
  end: string
}

export interface WorkingSchedule {
  days: Record<WeekdayIndex, DaySchedule>
  breakTime: WorkingScheduleBreak
  // null = follow the account's own timezone (Settings > Profile); a value here
  // overrides it for this calendar's own display/scheduling context only.
  timezone: string | null
}

const DEFAULT_DAY_HOURS = { start: "08:00", end: "18:00" }

// Mirrors the product's existing Work Week convention (Calendar.tsx's
// timeGridWorkWeek already hides Sat/Sun via hiddenDays: [0, 6]) rather than
// inventing a new default — see docs/kb for the calendar-settings decision.
export const DEFAULT_WORKING_SCHEDULE: WorkingSchedule = {
  days: {
    0: { enabled: false, ...DEFAULT_DAY_HOURS },
    1: { enabled: true, ...DEFAULT_DAY_HOURS },
    2: { enabled: true, ...DEFAULT_DAY_HOURS },
    3: { enabled: true, ...DEFAULT_DAY_HOURS },
    4: { enabled: true, ...DEFAULT_DAY_HOURS },
    5: { enabled: true, ...DEFAULT_DAY_HOURS },
    6: { enabled: false, ...DEFAULT_DAY_HOURS }
  },
  breakTime: { enabled: false, start: "13:00", end: "14:00" },
  timezone: null
}

export const CALENDAR_WORKING_SCHEDULE_KEY = surfaceKey(
  "calendar.working-schedule"
)

const isDaySchedule = (value: unknown): value is DaySchedule =>
  !!value &&
  typeof value === "object" &&
  typeof (value as DaySchedule).enabled === "boolean" &&
  typeof (value as DaySchedule).start === "string" &&
  typeof (value as DaySchedule).end === "string"

/**
 * Fills in anything missing/malformed with the default — a stored value can
 * predate a field this shape later gained (redux-persist's autoMergeLevel1
 * trap, see ui-preferences.selectors.ts) or simply be `unknown` off the wire.
 */
export function normalizeWorkingSchedule(raw: unknown): WorkingSchedule {
  const input = (
    raw && typeof raw === "object" ? raw : {}
  ) as Partial<WorkingSchedule>

  const days = ALL_WEEKDAYS.reduce(
    (acc, day) => {
      const candidate = input.days?.[day]
      acc[day] = isDaySchedule(candidate)
        ? candidate
        : DEFAULT_WORKING_SCHEDULE.days[day]
      return acc
    },
    {} as Record<WeekdayIndex, DaySchedule>
  )

  const breakInput = input.breakTime
  const breakTime: WorkingScheduleBreak =
    breakInput &&
    typeof breakInput === "object" &&
    typeof breakInput.enabled === "boolean" &&
    typeof breakInput.start === "string" &&
    typeof breakInput.end === "string"
      ? breakInput
      : DEFAULT_WORKING_SCHEDULE.breakTime

  const timezone = typeof input.timezone === "string" ? input.timezone : null

  return { days, breakTime, timezone }
}

const toMinutes = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number)
  return hours * 60 + minutes
}

/**
 * Whether the break fits entirely inside a given enabled day's hours — the
 * one rule `scheduleToBusinessHours` and `scheduleToBreakEvent` both need,
 * kept in one place so "does the break apply here" can never drift between
 * the shading and the labeled break block that sits on top of it.
 */
function breakFitsDay(schedule: WorkingSchedule, day: WeekdayIndex): boolean {
  const { start, end } = schedule.days[day]
  const { start: breakStart, end: breakEnd } = schedule.breakTime
  return (
    toMinutes(breakStart) < toMinutes(breakEnd) &&
    toMinutes(start) <= toMinutes(breakStart) &&
    toMinutes(breakEnd) <= toMinutes(end)
  )
}

export interface WorkingScheduleValidation {
  dayErrors: Partial<Record<WeekdayIndex, string>>
  breakError: string | null
  isValid: boolean
}

/**
 * Pure so it can be unit-tested directly and reused by both the drawer's
 * inline messages and the guard that blocks Save.
 */
export function validateWorkingSchedule(
  schedule: WorkingSchedule
): WorkingScheduleValidation {
  const dayErrors: Partial<Record<WeekdayIndex, string>> = {}
  const enabledDays = ALL_WEEKDAYS.filter((day) => schedule.days[day].enabled)

  enabledDays.forEach((day) => {
    const { start, end } = schedule.days[day]
    if (toMinutes(start) >= toMinutes(end)) {
      dayErrors[day] = "Start time must be before end time."
    }
  })

  let breakError: string | null = null
  const { enabled, start, end } = schedule.breakTime

  if (enabled) {
    if (toMinutes(start) >= toMinutes(end)) {
      breakError = "Break start time must be before break end time."
    } else {
      const daysBreakDoesNotFit = enabledDays.filter((day) => {
        if (dayErrors[day]) return false // that day's own hours are already invalid
        const daySchedule = schedule.days[day]
        return !(
          toMinutes(daySchedule.start) <= toMinutes(start) &&
          toMinutes(end) <= toMinutes(daySchedule.end)
        )
      })

      if (daysBreakDoesNotFit.length > 0) {
        const dayNames = daysBreakDoesNotFit
          .map((day) => WEEKDAY_LABELS[day])
          .join(", ")
        breakError = `Break must fall within working hours on ${dayNames}.`
      }
    }
  }

  return {
    dayErrors,
    breakError,
    isValid: Object.keys(dayErrors).length === 0 && !breakError
  }
}

export interface BusinessHoursEntry {
  daysOfWeek: number[]
  startTime: string
  endTime: string
}

/**
 * Working days/hours/break -> FullCalendar's `businessHours` shape. FullCalendar
 * shades everything outside these ranges as non-business time on its own — no
 * custom rendering needed (see Calendar.tsx). A break splits its day into two
 * segments; an invalid day/break (start >= end) is skipped defensively rather
 * than producing a negative-length segment, so a mid-edit draft never crashes
 * the grid — Save itself is separately blocked by validateWorkingSchedule.
 */
export function scheduleToBusinessHours(
  schedule: WorkingSchedule
): BusinessHoursEntry[] {
  const daysByRange = new Map<string, number[]>()

  ALL_WEEKDAYS.forEach((day) => {
    const daySchedule = schedule.days[day]
    if (!daySchedule.enabled) return
    if (toMinutes(daySchedule.start) >= toMinutes(daySchedule.end)) return

    const ranges: Array<[string, string]> =
      schedule.breakTime.enabled && breakFitsDay(schedule, day)
        ? [
            [daySchedule.start, schedule.breakTime.start],
            [schedule.breakTime.end, daySchedule.end]
          ]
        : [[daySchedule.start, daySchedule.end]]

    ranges.forEach(([start, end]) => {
      if (toMinutes(start) >= toMinutes(end)) return
      const key = `${start}-${end}`
      const days = daysByRange.get(key) ?? []
      days.push(day)
      daysByRange.set(key, days)
    })
  })

  return Array.from(daysByRange.entries()).map(([key, daysOfWeek]) => {
    const [startTime, endTime] = key.split("-")
    return { daysOfWeek, startTime, endTime }
  })
}

/**
 * Days to hide from the "Work Week" view — everything not configured as a
 * working day. Guards against hiding all 7 (FullCalendar can't render that)
 * by falling back to showing every day.
 */
export function scheduleToWorkWeekHiddenDays(
  schedule: WorkingSchedule
): number[] {
  const hidden = ALL_WEEKDAYS.filter((day) => !schedule.days[day].enabled)
  return hidden.length >= 7 ? [] : hidden
}

export function areEnabledDaysUniform(schedule: WorkingSchedule): boolean {
  const enabledDays = ALL_WEEKDAYS.filter((day) => schedule.days[day].enabled)
  if (enabledDays.length <= 1) return true
  const [first, ...rest] = enabledDays.map((day) => schedule.days[day])
  return rest.every((day) => day.start === first.start && day.end === first.end)
}

export interface VisibleTimeRange {
  start: string // "HH:mm"
  end: string
}

// No configured day to anchor on — show the full day rather than collapsing
// the grid to nothing.
const FULL_DAY_RANGE: VisibleTimeRange = { start: "00:00", end: "23:59" }

/**
 * The Work Week view's own visible range: earliest configured start ->
 * latest configured end, across enabled days only. Deliberately never
 * widened to fit stray events outside it (unlike CalendarAvailability's
 * computeAvailabilityWindow, which does expand for events) — the working
 * schedule is the sole source of truth for this view's viewport, not the
 * data happening to be loaded; an event outside it stays reachable from
 * Day/Week, whose ranges this doesn't touch.
 */
export function scheduleToVisibleTimeRange(
  schedule: WorkingSchedule
): VisibleTimeRange {
  const enabledDays = ALL_WEEKDAYS.filter(
    (day) =>
      schedule.days[day].enabled &&
      toMinutes(schedule.days[day].start) < toMinutes(schedule.days[day].end)
  )
  if (enabledDays.length === 0) return FULL_DAY_RANGE

  return enabledDays.reduce<VisibleTimeRange>((range, day) => {
    const { start, end } = schedule.days[day]
    return {
      start: toMinutes(start) < toMinutes(range.start) ? start : range.start,
      end: toMinutes(end) > toMinutes(range.end) ? end : range.end
    }
  }, schedule.days[enabledDays[0]])
}

export interface BreakEvent {
  start: string
  end: string
  title: string
  editable: false
  startEditable: false
  durationEditable: false
  classNames: string[]
  extendedProps: { isBreak: true }
}

// How far around `referenceDate` to materialize break instances. FullCalendar
// v6's `display: "background"` events don't render at all in this project's
// installed packages — confirmed with a minimal, isolated FullCalendar
// instance carrying none of this app's own code: a plain and a background
// event at the same time, only the plain one produced any `.fc-event`/
// `.fc-bg-event` DOM node (businessHours still works because it's parsed
// through its own separate, dedicated code path, not the `events` array's).
// So the break is a real (non-background), non-editable event instead —
// styled to read as a spanning unavailable band in calendar.less
// (`.calendar-break-event`) — which means it needs concrete dates the way
// any other event does; there is no recurring-definition shortcut available
// here either way. The cost is a bound: navigating past this window (about 4
// months either side of when the schedule was last read) shows no break
// block until the schedule/timezone next changes and the window re-centers.
const BREAK_WINDOW_DAYS_PAST = 14
const BREAK_WINDOW_DAYS_FUTURE = 120

// Applied via FullCalendar's own `classNames`, not inline styles, so
// calendar.less owns the actual look — kept here as the one place the class
// name and the "is this a break" check both come from.
const BREAK_EVENT_CLASS_NAME = "calendar-break-event"

/**
 * The break as a set of concrete, dated, non-editable events (never sent to
 * the API, never draggable — `editable`/`startEditable`/`durationEditable`
 * are all `false` — and excluded from list/month views by the caller, since
 * a spanning band reads as a real row/cell there) — one per qualifying date
 * in the window around `referenceDate`. Empty when the break is off or fits
 * no enabled day, so disabling it (or saving a break the schedule no longer
 * has room for) removes every instance on the very next render — nothing to
 * separately tear down.
 */
export function scheduleToBreakEvents(
  schedule: WorkingSchedule,
  timezone: string,
  referenceDate: Dayjs = dayjs().tz(timezone)
): BreakEvent[] {
  if (!schedule.breakTime.enabled) return []

  const applicableDays = new Set<WeekdayIndex>(
    ALL_WEEKDAYS.filter(
      (day) => schedule.days[day].enabled && breakFitsDay(schedule, day)
    )
  )
  if (applicableDays.size === 0) return []

  const [startHour, startMinute] = schedule.breakTime.start
    .split(":")
    .map(Number)
  const [endHour, endMinute] = schedule.breakTime.end.split(":").map(Number)

  const events: BreakEvent[] = []
  for (
    let offset = -BREAK_WINDOW_DAYS_PAST;
    offset <= BREAK_WINDOW_DAYS_FUTURE;
    offset++
  ) {
    const day = referenceDate.add(offset, "day")
    if (!applicableDays.has(day.day() as WeekdayIndex)) continue

    events.push({
      start: day
        .hour(startHour)
        .minute(startMinute)
        .second(0)
        .millisecond(0)
        .format(),
      end: day
        .hour(endHour)
        .minute(endMinute)
        .second(0)
        .millisecond(0)
        .format(),
      title: "Break",
      editable: false,
      startEditable: false,
      durationEditable: false,
      classNames: [BREAK_EVENT_CLASS_NAME],
      extendedProps: { isBreak: true }
    })
  }

  return events
}
